import React, { Suspense } from "react";
import { BrowserRouter } from "react-router-dom";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import keycloak, { keycloakInitOptions } from "./Keycloak";
import { TimezoneProvider } from "./context/TimezoneContext";
import { useTranslation } from "react-i18next";
import { useUiVersion } from "./components/common/useUiVersion";
import LazyTreeBoundary, { clearLazyTreeReloadLatch } from "./components/common/LazyTreeBoundary";
// GUNDI-5665: new is the default tree, so it is imported directly — every
// visit renders it, and a lazy chunk would only add a fetch and a skeleton
// flash to the common path. (Under GUNDI-5642 the roles were reversed: legacy
// was eager and new was the lazy opt-in.)
import NewApp from "./NewApp";

// GUNDI-5665: the frozen legacy portal, in its own chunk so browsers that never
// take the banner's opt-out never download it.
const LegacyApp = React.lazy(() =>
  import("./legacy/LegacyApp").then((module) => {
    // Chunk loaded: a past reload-recovery latch is spent, arm the next one.
    clearLazyTreeReloadLatch();
    return module;
  })
);

// Skeleton while the legacy chunk downloads: no copy, so it needs no keys.
function TreeLoading() {
  return (
    <div className="px-8 desktop:px-[60px] py-6 pt-[115px]" data-testid="TreeLoading-div">
      <div className="h-8 w-64 mb-6 bg-neutral-gray-50 rounded animate-pulse" />
      {[...Array(8)].map((_, i) => (
        <div key={i} className="h-4 w-full max-w-3xl mb-3 bg-neutral-gray-50 rounded animate-pulse" />
      ))}
    </div>
  );
}

function TreeLoadFailed() {
  const { t } = useTranslation();
  return (
    <div className="px-8 desktop:px-[60px] py-6 pt-[115px] text-base text-off-black" data-testid="TreeLoadFailed-div">
      {t("Something went wrong. Please try again or contact support.")}
    </div>
  );
}

// The version flag alone decides which tree owns the paths — both trees keep
// their own full route table, their own Nav, help panel, toasts and 404, and
// only one of them ever mounts. Mounting one tree inside the other's chrome
// instead would put the wrong navbar over the pages.
function VersionedTree() {
  const version = useUiVersion();
  if (version === "legacy") {
    return (
      <LazyTreeBoundary fallback={<TreeLoadFailed />}>
        <Suspense fallback={<TreeLoading />}>
          <LegacyApp />
        </Suspense>
      </LazyTreeBoundary>
    );
  }
  return <NewApp />;
}

function App() {
  return (
    <div className="min-h-screen bg-very-light-gray">
      <ReactKeycloakProvider authClient={keycloak} initOptions={keycloakInitOptions}>
        <TimezoneProvider>
          <BrowserRouter>
            <VersionedTree />
          </BrowserRouter>
        </TimezoneProvider>
      </ReactKeycloakProvider>
    </div>
  );
}

export default App;
